import type { PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'

import { RequestIdleCallbackHelper } from '@space-shell/common/utils/requestIdleCallback'

RequestIdleCallbackHelper.setup()

const IDLE_RENDERER_MIN_TIME = 10
const IDLE_RENDERER_REQUIRED_FRAMES = 3
const IDLE_RENDERER_EXTRA_DELAY = 0
const IDLE_RENDERER_MAX_WAIT = 5000

type IdleRendererProps = PropsWithChildren<{
  extraDelay?: number
  minIdleTime?: number
  requiredIdleFrames?: number
  maxWait?: number
  loadingElement?: React.ReactNode | HTMLElement
}>

const IdleRenderer = ({
  children,
  extraDelay = IDLE_RENDERER_EXTRA_DELAY,
  minIdleTime = IDLE_RENDERER_MIN_TIME,
  requiredIdleFrames = IDLE_RENDERER_REQUIRED_FRAMES,
  maxWait = IDLE_RENDERER_MAX_WAIT,
  loadingElement = null
}: IdleRendererProps) => {
  const [shouldRender, setShouldRender] = useState(false)
  const idleFramesCount = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maxWaitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const renderTriggeredRef = useRef(false)

  useEffect(() => {
    let isMounted = true
    let currentRequestId: number | null = null

    const render = () => {
      if (!isMounted || renderTriggeredRef.current) return
      renderTriggeredRef.current = true
      setShouldRender(true)
    }

    const renderWithDelay = () => {
      if (extraDelay > 0) {
        timeoutRef.current = setTimeout(render, extraDelay)
      } else {
        render()
      }
    }

    maxWaitTimeoutRef.current = setTimeout(renderWithDelay, maxWait)

    const scheduleIdleCallback = () => {
      if (renderTriggeredRef.current) return

      currentRequestId = requestIdleCallback(
        (deadline) => {
          if (!isMounted || renderTriggeredRef.current) return

          if (deadline.timeRemaining() >= minIdleTime || deadline.didTimeout) {
            idleFramesCount.current += 1

            if (idleFramesCount.current >= requiredIdleFrames) {
              renderWithDelay()
            } else {
              Promise.resolve().then(scheduleIdleCallback)
            }
          } else {
            idleFramesCount.current = 0
            Promise.resolve().then(scheduleIdleCallback)
          }
        },
        { timeout: 1000 }
      )
    }

    scheduleIdleCallback()

    return () => {
      isMounted = false
      if (currentRequestId !== null) {
        cancelIdleCallback(currentRequestId)
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
      if (maxWaitTimeoutRef.current !== null) {
        clearTimeout(maxWaitTimeoutRef.current)
      }
    }
  }, [extraDelay, minIdleTime, requiredIdleFrames, maxWait])

  if (!shouldRender && loadingElement) {
    return <>{loadingElement}</>
  }

  if (!shouldRender) return null

  return <>{children}</>
}

export default IdleRenderer
